import { FunctionComponent, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Modal, Icon } from "@/components";
import { ButtonColor } from "@/components/Button/types";

import { FieldMappingAiConfig } from "../../schema/hubspot";
import { AiConfigFormPanel } from "./AiConfigFormPanel";
import { AiTestPanel } from "./AiTestPanel";
import {
	AiConfigFormValues,
	AiConfigLabels,
	AiConfigModalCallbacks,
	AiConfigTargetField,
} from "./aiConfig.types";
import {
	buildDefaultPrompt,
	createAiConfigSchema,
	isTextLikeField,
	toAiConfigPayload,
} from "./aiConfig.utils";

interface AiConfigModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	subtitle?: string;
	targetField?: AiConfigTargetField;
	allFields: AiConfigTargetField[];
	initialConfig?: FieldMappingAiConfig;
	labels: AiConfigLabels;
	callbacks: AiConfigModalCallbacks;
	onSave: (config: FieldMappingAiConfig) => void | Promise<void>;
}

export const AiConfigModal: FunctionComponent<AiConfigModalProps> = ({
	isOpen,
	onClose,
	title,
	subtitle,
	targetField,
	allFields,
	initialConfig,
	labels,
	callbacks,
	onSave,
}) => {
	const textFields = useMemo(() => allFields.filter(isTextLikeField), [allFields]);
	const allowedValues = useMemo(
		() => targetField?.options?.map((option) => option.value) ?? [],
		[targetField]
	);
	const allowedValueLabels = useMemo(
		() => targetField?.options?.map((option) => option.label) ?? [],
		[targetField]
	);
	const schema = useMemo(
		() =>
			createAiConfigSchema(
				labels,
				allowedValues,
				textFields.map((field) => field.name)
			),
		[allowedValues, labels, textFields]
	);
	const form = useForm<AiConfigFormValues>({
		mode: "onChange",
		resolver: zodResolver(schema),
		defaultValues: {
			prompt: initialConfig?.prompt ?? buildDefaultPrompt(targetField, labels.defaultPrompt),
			fallbackValue: initialConfig?.fallbackValue ?? "",
			syncReasoning: Boolean(initialConfig?.syncReasoningTargetField),
			syncReasoningTargetField: initialConfig?.syncReasoningTargetField ?? "",
			syncSource: Boolean(initialConfig?.syncSourceTargetField),
			syncSourceTargetField: initialConfig?.syncSourceTargetField ?? "",
		},
	});
	const prompt = form.watch("prompt");
	const fallbackValue = form.watch("fallbackValue");

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		form.reset({
			prompt: initialConfig?.prompt ?? buildDefaultPrompt(targetField, labels.defaultPrompt),
			fallbackValue: initialConfig?.fallbackValue ?? "",
			syncReasoning: Boolean(initialConfig?.syncReasoningTargetField),
			syncReasoningTargetField: initialConfig?.syncReasoningTargetField ?? "",
			syncSource: Boolean(initialConfig?.syncSourceTargetField),
			syncSourceTargetField: initialConfig?.syncSourceTargetField ?? "",
		});
	}, [form, initialConfig, isOpen, labels.defaultPrompt, targetField]);

	const handleSave = form.handleSubmit(async (values) => {
		await onSave(toAiConfigPayload(values));
	});

	return (
		<Modal
			cancelButtonText={labels.cancel}
			disableSave={!form.formState.isValid}
			handleClose={onClose}
			handleSave={handleSave}
			icon={<Icon className="w-5 h-5 text-purple-800" icon={faSparkles} />}
			isOpened={isOpen}
			size="xl"
			submitButtonColor={ButtonColor.BRAND}
			submitButtonText={labels.save}
			subtitle={undefined}
			title={title}
			zIndex={1100}
		>
			<div className="mb-4 rounded-2xl border border-border-light bg-background px-4 py-3">
				<div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
					{subtitle}
				</div>
			</div>
			<div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
				<AiConfigFormPanel
					form={form}
					labels={labels}
					targetField={targetField}
					textFields={textFields}
				/>
				<AiTestPanel
					allowedValues={allowedValueLabels}
					callbacks={callbacks}
					fallbackValue={fallbackValue}
					labels={labels}
					prompt={prompt}
				/>
			</div>
			<div className="mt-4 rounded-xl border border-border-light bg-brand-white px-3 py-2 text-xs font-semibold text-text-meta">
				{labels.costReminder}
			</div>
		</Modal>
	);
};
